import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Icon, buttonClasses, type BadgeTone } from "@sagot/ui";
import { usePermissions } from "../../../../session/permissions";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { UsageMeter } from "../../billing/usage-meter";
import { useFormatters } from "../../shared";
import { resetLabel, usageView } from "./launch-tier-model";
import type { LaunchSettings } from "./use-launch-settings";

const K = "launch.tier";
const TIER_TONES: Readonly<Record<LaunchSettings["plan"]["tier"], BadgeTone>> = { none: "neutral", lite: "brand", plus: "success" };

/** The way to Plus: people who manage billing get the link, everyone else is told whom to ask. */
function UpgradeAction() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  if (!usePermissions().can("billing.manage")) return <p className="text-xs text-fg-muted" data-testid="tier-ask">{t(`${K}.askOwner`)}</p>;
  return (
    <AppLink href={consoleRoutes.billing(summary.slug)} className={buttonClasses({ variant: "primary", size: "sm" })} data-testid="tier-upgrade">
      <Icon name="arrow-up" />
      {t(`${K}.upgrade`)}
    </AppLink>
  );
}

function Line({ included, children }: { included: boolean; children: string }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-start gap-2">
      <Icon name={included ? "check" : "x"} className={included ? "mt-0.5 size-4 shrink-0 text-success" : "mt-0.5 size-4 shrink-0 text-fg-subtle"} />
      <span className={included ? "" : "text-fg-muted"}>
        {children}
        <span className="sr-only"> {t(included ? `${K}.included` : `${K}.notIncluded`)}</span>
      </span>
    </li>
  );
}

function LiteUsage({ used, limit, resetsOn }: { used: number; limit: number; resetsOn: string }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const view = usageView(used, limit);
  const date = resetLabel(resetsOn, fmt.locale);
  return (
    <div className="space-y-2" data-testid="tier-usage" data-level={view.level}>
      <UsageMeter
        label={t(`${K}.usageLabel`)}
        used={view.used}
        limit={view.limit}
        usedLabel={fmt.number(view.used)}
        limitLabel={`/ ${fmt.number(view.limit)}`}
        hint={t(`${K}.resets`, { date })}
        hintWarns={view.level !== "ok"}
      />
      {view.level === "full" ? (
        <p role="alert" className="text-xs font-semibold text-danger" data-testid="tier-usage-full">
          {t(`${K}.usedUp`, { date })}
        </p>
      ) : null}
      {view.level === "warn" ? (
        <p role="status" className="text-xs font-semibold text-warning" data-testid="tier-usage-warn">
          {t(`${K}.almostUsed`, { count: view.remaining, date })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Settings > Secure launch, top card (DEC-151): which Secure launch the workspace has, what it includes, Lite's launches this
 * month against its 2,000, and where customer facts would be used, a way to Plus. Plus and Institution show no cap.
 */
export function LaunchTierCard({ settings }: { settings: LaunchSettings }) {
  const { t } = useTranslation();
  const plan = settings.plan;
  const lite = plan.tier === "lite";
  return (
    <section className="card" aria-labelledby="launch-tier-title" data-testid="launch-tier-card" data-tier={plan.tier}>
      <div className="card-header">
        <div className="min-w-0">
          <h2 id="launch-tier-title" className="card-title">
            {t(`${K}.title`)}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">{t(`${K}.subtitle_${plan.tier === "plus" ? "plus" : "lite"}`)}</p>
        </div>
        <Badge tone={TIER_TONES[plan.tier]} data-testid="tier-badge">
          {t(`${K}.name_${plan.tier}`)}
        </Badge>
      </div>
      <div className="space-y-4 px-5 py-4">
        {lite && plan.usage ? <LiteUsage used={plan.usage.used} limit={plan.usage.limit} resetsOn={plan.usage.resets_on} /> : null}
        {plan.tier === "plus" ? (
          <p className="text-sm" data-testid="tier-unlimited">
            {t(`${K}.unlimited`)}
          </p>
        ) : null}
        <ul className="space-y-2 text-sm" aria-label={t(`${K}.includesLabel`)}>
          <Line included>{t(`${K}.lineLaunch`)}</Line>
          <Line included>{t(`${K}.lineNamed`)}</Line>
          <Line included={plan.facts_included}>{t(`${K}.lineFacts`)}</Line>
        </ul>
        {lite ? (
          <div className="flex flex-wrap items-center gap-3 rounded-md bg-primary-soft p-3" role="note" data-testid="tier-prompt">
            <div className="min-w-0 flex-1 text-sm">
              <p className="font-semibold">{t(`${K}.promptTitle`)}</p>
              <p className="mt-0.5 text-xs text-fg-muted">{t(`${K}.promptBody`)}</p>
            </div>
            <UpgradeAction />
          </div>
        ) : null}
      </div>
    </section>
  );
}
